import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { Receipt, CreditCard } from '@mui/icons-material';
import { formatAmount, formatDate } from '../services/paymentService';

// Helper function to calculate pricing based on new model
const calculateMonthlyAmount = (roomCount) => {
  if (roomCount <= 3) {
    return 9.00;
  } else {
    const additionalRooms = roomCount - 3;
    return 9.00 + (additionalRooms * 3.00);
  }
};

// Helper function to generate pricing explanation
const getPricingExplanation = (fromRooms, toRooms, daysRemaining) => {
  const oldAmount = calculateMonthlyAmount(fromRooms);
  const newAmount = calculateMonthlyAmount(toRooms);
  const difference = Math.abs(newAmount - oldAmount);
  const prorationAmount = difference * (daysRemaining / 30);
  
  return {
    oldDescription: fromRooms <= 3 ? `${fromRooms} rooms: $9` : `3 rooms ($9) + ${fromRooms - 3} additional ($${(fromRooms - 3) * 3})`,
    newDescription: toRooms <= 3 ? `${toRooms} rooms: $9` : `3 rooms ($9) + ${toRooms - 3} additional ($${(toRooms - 3) * 3})`,
    difference: difference,
    prorationAmount: prorationAmount
  };
};

export default function ProrationConfirmationDialog({ 
  open, 
  onClose, 
  prorationData, 
  onConfirm,
  loading 
}) {
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  if (!prorationData) return null;

  const {
    currentRooms,
    newRooms,
    prorationAmount,
    newMonthlyAmount,
    currentMonthlyAmount,
    nextBillingDate,
    daysRemaining,
    changeType
  } = prorationData;

  const isUpgrade = changeType === 'upgrade';
  const roomDifference = Math.abs(newRooms - currentRooms);
  const pricingInfo = getPricingExplanation(currentRooms, newRooms, daysRemaining);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Receipt color="primary" />
          <Box>
            Confirm Plan {isUpgrade ? 'Upgrade' : 'Change'}
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Calculating proration...</Typography>
          </Box>
        ) : (
          <>
            {/* Current vs New Plan */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Plan Summary
              </Typography>
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Current Plan
                  </Typography>
                  <Typography variant="body1">
                    {currentRooms} room{currentRooms !== 1 ? 's' : ''} - {formatAmount(currentMonthlyAmount)}/month
                  </Typography>
                </Box>
                
                <Box textAlign="right">
                  <Typography variant="body2" color="text.secondary">
                    New Plan
                  </Typography>
                  <Typography variant="body1" color="primary">
                    {newRooms} room{newRooms !== 1 ? 's' : ''} - {formatAmount(newMonthlyAmount)}/month
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Prorated Charge */}
            <Card variant="outlined" sx={{ mb: 3, bgcolor: isUpgrade ? 'success.50' : 'warning.50' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    {isUpgrade ? 'Immediate Charge' : 'Immediate Credit'}
                  </Typography>
                  <Chip 
                    label={isUpgrade ? 'Charge Today' : 'Credit Applied'} 
                    color={isUpgrade ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
                
                <Typography variant="h4" color={isUpgrade ? 'success.main' : 'warning.main'} gutterBottom>
                  {isUpgrade ? '+' : '-'}{formatAmount(prorationAmount)}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  Prorated charge for {roomDifference} {isUpgrade ? 'additional' : 'fewer'} room{roomDifference !== 1 ? 's' : ''} 
                  {' '}for the remaining {daysRemaining} days of this billing period
                </Typography>
                
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Current plan:</strong> {pricingInfo.oldDescription}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>New plan:</strong> {pricingInfo.newDescription}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Calculation:</strong> ${pricingInfo.difference} difference × ({daysRemaining} days remaining / 30 days)
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Billing Timeline */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Billing Timeline
              </Typography>
              
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Today:</strong> {isUpgrade ? 'Charge' : 'Credit'} of {formatAmount(prorationAmount)}
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{formatDate(nextBillingDate)}:</strong> Next billing at {formatAmount(newMonthlyAmount)}/month
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  <strong>Going forward:</strong> {formatAmount(newMonthlyAmount)} charged monthly
                </Typography>
              </Box>
            </Box>

            {isUpgrade && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Your payment method will be charged {formatAmount(prorationAmount)} immediately. 
                  Your new {newRooms}-room plan will be active right away.
                </Typography>
              </Alert>
            )}

            {!isUpgrade && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  You'll receive a {formatAmount(prorationAmount)} credit. 
                  Your new {newRooms}-room plan will be active immediately.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={confirming}>
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained"
          disabled={loading || confirming}
          startIcon={confirming ? <CircularProgress size={20} /> : <CreditCard />}
        >
          {confirming ? 'Processing...' : `Confirm ${isUpgrade ? 'Upgrade' : 'Change'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 